import type { CouponAllocation } from "@/lib/api";

/**
 * Cupom por itens: funções puras de apresentação, isoladas de propósito (checkout, PDF e telas de pedido).
 * A regra de negócio (quantas unidades o cupom cobre e quanto desconta) é do backend; aqui só se lê o resultado.
 */

export interface OrderItemRow<T> {
  item: T;
  /** `coupon` = unidades bonificadas pelo cupom (linha separada, para auditoria); `normal` = demais unidades. */
  kind: "normal" | "coupon";
  quantity: number;
  /** Valor da linha: 0 nas unidades 100% cobertas pelo cupom; no percentual com itens, o que sobra após o desconto. */
  total: number;
  couponDiscountAmount: number;
}

/**
 * Telas de pedido (backoffice e cliente): o item com cupom por itens vira duas linhas — as unidades pagas e as
 * bonificadas pelo cupom. `itemTotal` já vem líquido do cupom. Itens sem cupom seguem como uma linha só.
 */
export function splitOrderItemRows<
  T extends { quantityOrdered: number; itemTotal: number; netPrice?: number; couponQuantity?: number; couponDiscountAmount?: number },
>(items: T[]): OrderItemRow<T>[] {
  return items.flatMap((item): OrderItemRow<T>[] => {
    const couponQuantity = Math.min(Math.max(item.couponQuantity ?? 0, 0), item.quantityOrdered);
    if (couponQuantity <= 0) {
      return [{ item, kind: "normal", quantity: item.quantityOrdered, total: item.itemTotal, couponDiscountAmount: 0 }];
    }
    const rows: OrderItemRow<T>[] = [];
    const paidQuantity = item.quantityOrdered - couponQuantity;
    const discount = item.couponDiscountAmount ?? 0;
    // Unidades do cupom: valor bruto delas menos o desconto (0 quando o cupom cobre 100%, como no cupom por item).
    const grossCovered = (item.netPrice ?? 0) * couponQuantity;
    const couponTotal = grossCovered - discount > 0.01 ? Math.round((grossCovered - discount) * 100) / 100 : 0;
    if (paidQuantity > 0) {
      rows.push({ item, kind: "normal", quantity: paidQuantity, total: Math.round((item.itemTotal - couponTotal) * 100) / 100, couponDiscountAmount: 0 });
    }
    rows.push({ item, kind: "coupon", quantity: couponQuantity, total: couponTotal, couponDiscountAmount: discount });
    return rows;
  });
}

/** Item bonificado por cupom, como o PDF o recebe (a descrição vem do item do pedido). */
export interface PdfCouponItem {
  productCode: string;
  description: string;
  quantity: number;
  discountAmount: number;
}

/**
 * PDF com `COUPON_ITEM_DISCOUNT_AS_VALUE` ligado: o item do cupom sai em UMA linha (quantidade total, Valor unitário
 * do cliente) com o total já descontado — sem linha de bonificado e sem "Desconto do cupom" separado. Devolve cópias;
 * não altera os itens recebidos. O desconto de cada produto é abatido das linhas desse produto, na ordem.
 */
export function applyCouponDiscountToPdfItems<T extends { codigo_produto: string; valor_total: number }>(
  items: T[],
  couponItems: PdfCouponItem[],
): T[] {
  const remaining = new Map<string, number>();
  for (const couponItem of couponItems) {
    remaining.set(couponItem.productCode, (remaining.get(couponItem.productCode) ?? 0) + couponItem.discountAmount);
  }
  return items.map((item) => {
    const left = remaining.get(item.codigo_produto) ?? 0;
    if (left <= 0 || item.valor_total <= 0) return item;
    const discount = Math.min(left, item.valor_total);
    remaining.set(item.codigo_produto, Math.round((left - discount) * 100) / 100);
    return { ...item, valor_total: Math.round((item.valor_total - discount) * 100) / 100 };
  });
}

/** Cupom com unidades cobertas (por item, ou percentual com itens): o checkout/PDF mostram as quantidades cobertas. */
export const hasCouponUnits = (coupon: { allocations?: CouponAllocation[] } | null | undefined): boolean =>
  (coupon?.allocations ?? []).some((allocation) => (allocation.couponQuantity ?? 0) > 0);

/**
 * Cupom que abate o pedido todo (percentual sem itens): sem unidades cobertas, o desconto é rateado entre as linhas
 * proporcionalmente ao valor de cada uma (resíduo de centavos na última), para o PDF mostrar valores já descontados
 * em todos os blocos (itens, resumo por segmento, total do extrato). `quantity` fica 0: não há coluna de cobertos.
 */
export function distributeCouponDiscount(
  lines: { productCode: string; description: string; total: number }[],
  discount: number,
): PdfCouponItem[] {
  const eligible = lines.filter((line) => line.total > 0);
  const base = eligible.reduce((sum, line) => sum + line.total, 0);
  if (discount <= 0 || base <= 0) return [];
  let left = Math.round(Math.min(discount, base) * 100) / 100;
  return eligible.map((line, index) => {
    const isLast = index === eligible.length - 1;
    const share = isLast
      ? left
      : Math.min(Math.round(((discount * line.total) / base) * 100) / 100, line.total, left);
    left = Math.round((left - share) * 100) / 100;
    return { productCode: line.productCode, description: line.description, quantity: 0, discountAmount: Math.min(share, line.total) };
  }).filter((item) => item.discountAmount > 0);
}

export interface CouponBonifiedLine {
  itemKey: string;
  productCode: string;
  description: string;
  quantity: number;
  discountAmount: number;
  /** Segmento e série do item do pedido, para identificar a linha no resumo (opcionais). */
  segment?: string | null;
  series?: string | null;
}

/** Itens bonificados pelo cupom (alocações com unidades cobertas), com a descrição vinda do item do pedido. */
export function buildCouponBonifiedLines(
  allocations: CouponAllocation[] | undefined,
  describe: (allocation: CouponAllocation) => string | undefined,
  /** Segmento/série do item da alocação (opcional; o PDF não usa). */
  details?: (allocation: CouponAllocation) => { segment?: string | null; series?: string | null } | undefined,
): CouponBonifiedLine[] {
  return (allocations ?? [])
    .filter((allocation) => (allocation.couponQuantity ?? 0) > 0)
    .map((allocation) => {
      const extra = details?.(allocation);
      return {
        itemKey: allocation.itemKey,
        productCode: allocation.productCode,
        description: describe(allocation) ?? allocation.productCode,
        quantity: allocation.couponQuantity ?? 0,
        discountAmount: allocation.discountAmount,
        ...(extra?.segment ? { segment: extra.segment } : {}),
        ...(extra?.series ? { series: extra.series } : {}),
      };
    });
}
